import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
  /** Whether the dialog is open. Controlled by the caller. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short imperative title — e.g. "Delete webhook?". */
  title: string;
  /** Optional body copy explaining what happens. Use for irreversible ops. */
  description?: React.ReactNode;
  /** Action label. Defaults to "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Tone of the primary action. `destructive` paints it red. */
  tone?: 'destructive' | 'primary';
  /** Called when the user confirms. Awaited; spinner shown while pending. */
  onConfirm: () => void | Promise<void>;
}

/**
 * Shared confirmation dialog. Replaces `window.confirm()` so the app
 * keeps a single visual language for destructive / irreversible ops.
 * Focus-trapped, escape-closes, click-outside dismisses — all via the
 * shared Dialog primitive.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'destructive',
  onConfirm,
}: Props) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    try {
      setBusy(true);
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            {tone === 'destructive' && (
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-destructive/10 text-destructive">
                <AlertTriangle className="h-4 w-4" />
              </span>
            )}
            <div className="space-y-1">
              <DialogTitle>{title}</DialogTitle>
              {description && (
                <p className="text-sm text-muted-foreground">{description}</p>
              )}
            </div>
          </div>
        </DialogHeader>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
